var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/core/events.ts
import EventEmitter from "events";
var SessionRequestEvent = {
  request: "walletconnectSessionRequest",
  approved: "walletconnectSessionRequest::approved",
  rejected: "walletconnectSessionRequest::rejected",
  connected: "walletconnectSessionRequest::connected"
};
var SwitchChainEvent = {
  request: "walletconnectSwitchChain",
  approved: "walletconnectSwitchChain::approved",
  rejected: "walletconnectSwitchChain::rejected"
};
var events = new EventEmitter();
events.setMaxListeners(100);

// src/core/walletconnectManager.ts
import { Core } from "@walletconnect/core";
import { SingleEthereum } from "@walletconnect/se-sdk";
import { getSdkError } from "@walletconnect/utils";

// src/utils/parseUri.ts
import { parseRelayParams } from "@walletconnect/utils";
import qs from "qs";
var parseWalletConnectUri = (uri) => {
  const str = uri.startsWith("wc://") ? uri.replace("wc://", "wc:") : uri;
  const pathStart = str.indexOf(":");
  const pathEnd = str.indexOf("?") !== -1 ? str.indexOf("?") : void 0;
  const protocol = str.substring(0, pathStart);
  const path = str.substring(pathStart + 1, pathEnd);
  const requiredValues = path.split("@");
  const queryString = typeof pathEnd !== "undefined" ? str.substring(pathEnd) : "";
  const queryParams = qs.parse(queryString);
  const result = {
    protocol,
    topic: requiredValues[0],
    version: parseInt(requiredValues[1], 10),
    symKey: queryParams.symKey,
    relay: parseRelayParams(queryParams)
  };
  return result;
};
var getRealUri = (url) => {
  var _a;
  if (url.startsWith("http")) {
    const params = getAllUrlParams(url);
    return decodeURIComponent((_a = params["uri"]) != null ? _a : "");
  }
  return url;
};
var isValidWCURI = (uri) => {
  const result = parseWalletConnectUri(uri);
  if (result.version === 2) {
    return !(!result.topic || !result.symKey || !result.relay);
  }
  return false;
};
var getAllUrlParams = (url) => {
  var _a;
  const queryString = (_a = url.split("?")) == null ? void 0 : _a[1];
  const obj = {};
  if (queryString) {
    queryString.split("&").forEach((param) => {
      const [key, value] = param.split("=");
      obj[key] = value;
    });
  }
  return obj;
};
var parseUri_default = parseWalletConnectUri;

// src/core/constants.ts
var constants_default = {
  WALLET_CONNECT: {
    SESSION_LIFETIME: 24,
    LIMIT_SESSIONS: 20
  },
  CLIENT_OPTIONS: {
    clientMeta: {
      description: `"Powered by Particle Network's MPC-TSS technology."`,
      url: "https://wallet.particle.network",
      icons: ["https://static.particle.network/logo-small.png"],
      name: "Particle Wallet"
    }
  }
};

// src/core/backgroundBridge.ts
var BackgroundBridge = class {
  constructor(options) {
    this.options = options;
    this.chainChanged = (chainId) => __async(this, null, function* () {
      const { provider, wcRequestActions } = this.options;
      const accounts = yield provider.request({
        method: "eth_requestAccounts"
      });
      yield wcRequestActions.updateSession({
        chainId: Number(chainId),
        accounts
      });
    });
    this.accountsChanged = (accounts) => __async(this, null, function* () {
      if (accounts.length > 0) {
        const { provider, wcRequestActions } = this.options;
        const chainId = yield provider.request({
          method: "eth_chainId"
        });
        yield wcRequestActions.updateSession({
          chainId: Number(chainId),
          accounts
        });
      }
    });
    const { provider } = options;
    provider.on("accountsChanged", this.accountsChanged);
    provider.on("chainChanged", this.chainChanged);
  }
  request(payload, requestId) {
    return __async(this, null, function* () {
      var _a;
      const { provider, wcRequestActions } = this.options;
      try {
        if (payload.method === "eth_signTypedData") {
          payload.method = "eth_signTypedData_v4";
        }
        const result = yield provider.request(payload);
        yield wcRequestActions.approveRequest({
          id: requestId,
          result
        });
      } catch (error) {
        yield wcRequestActions.rejectRequest({
          id: requestId,
          error: {
            code: (_a = error == null ? void 0 : error.code) != null ? _a : 1e4,
            message: (error == null ? void 0 : error.message) || (typeof error === "string" ? error : "Unknown error")
          }
        });
      }
    });
  }
};

// src/core/walletconnectV2.ts
var ERROR_MESSAGES = {
  INVALID_CHAIN: "Invalid chainId",
  MANUAL_DISCONNECT: "Manual disconnect",
  USER_REJECT: "User reject",
  AUTO_REMOVE: "Automatic removal",
  INVALID_ID: "Invalid Id"
};
var getPeerDataFromSessopn = (session) => {
  var _a, _b, _c;
  const url = (_a = session.peer.metadata.url) != null ? _a : "";
  const name = (_b = session.peer.metadata.name) != null ? _b : "";
  const description = (_c = session.peer.metadata.description) != null ? _c : "";
  const icons = session.peer.metadata.icons;
  return {
    url,
    name,
    description,
    icons
  };
};
var WalletConnect2Session = class {
  constructor({
    web3Wallet,
    session,
    provider
  }) {
    this.topicByRequestId = {};
    this.approveRequest = (_0) => __async(this, [_0], function* ({ id, result }) {
      const topic = this.topicByRequestId[id];
      try {
        yield this.web3Wallet.approveRequest({
          id,
          topic,
          result
        });
      } catch (err) {
      }
    });
    this.rejectRequest = (_0) => __async(this, [_0], function* ({ id, error }) {
      const topic = this.topicByRequestId[id];
      try {
        yield this.web3Wallet.rejectRequest({
          id,
          topic,
          error
        });
      } catch (err) {
      }
    });
    this.updateSession = (_0) => __async(this, [_0], function* ({ chainId, accounts }) {
      try {
        yield this.web3Wallet.updateSession({
          topic: this.session.topic,
          chainId,
          accounts
        });
      } catch (err) {
      }
    });
    this.handleRequest = (requestEvent) => __async(this, null, function* () {
      this.topicByRequestId[requestEvent.id] = requestEvent.topic;
      const method = requestEvent.params.request.method;
      const chainId = requestEvent.params.chainId;
      const methodParams = requestEvent.params.request.params;
      const selectedChainId = yield this.provider.request({ method: "eth_chainId" });
      if (Number(selectedChainId) !== Number(chainId)) {
        yield this.web3Wallet.rejectRequest({
          id: requestEvent.id,
          topic: this.session.topic,
          error: { code: 1, message: ERROR_MESSAGES.INVALID_CHAIN }
        });
        return;
      }
      yield this.backgroundBridge.request({ method, params: methodParams }, requestEvent.id);
    });
    this.web3Wallet = web3Wallet;
    this.session = session;
    this.provider = provider;
    this.peerMeta = getPeerDataFromSessopn(session);
    this.backgroundBridge = new BackgroundBridge({
      provider,
      wcRequestActions: {
        approveRequest: this.approveRequest,
        rejectRequest: this.rejectRequest,
        updateSession: this.updateSession
      }
    });
  }
};

// src/core/walletconnectManager.ts
var wc2ManagerInstance;
var _switchChainRequest;
var _WC2Manager = class {
  constructor({
    web3Wallet,
    provider,
    chainId,
    accounts
  }) {
    this.sessions = {};
    __privateAdd(this, _switchChainRequest, (requestEvent, session) => __async(this, null, function* () {
      var _a, _b;
      const params = requestEvent.params.request.params;
      const peerInfo = {
        peerId: requestEvent.id,
        peerMeta: session.peerMeta
      };
      try {
        yield new Promise((resolve, reject) => {
          events.once(SwitchChainEvent.approved, (peerId) => {
            if (peerInfo.peerId === peerId) {
              resolve(true);
            }
          });
          events.once(SwitchChainEvent.rejected, (peerId) => {
            if (peerInfo.peerId === peerId) {
              reject(new Error(SwitchChainEvent.rejected));
            }
          });
          events.emit(SwitchChainEvent.request, {
            session: peerInfo,
            chainId: params[0].chainId
          });
        });
      } catch (error) {
        yield this.web3Wallet.rejectRequest({
          id: requestEvent.id,
          topic: requestEvent.topic,
          error: { code: (_a = error.code) != null ? _a : 1, message: (_b = error.message) != null ? _b : ERROR_MESSAGES.USER_REJECT }
        });
        return;
      }
      yield this.web3Wallet.approveRequest({
        id: requestEvent.id,
        topic: requestEvent.topic,
        result: null
      });
    }));
    this.web3Wallet = web3Wallet;
    this.provider = provider;
    const sessions = web3Wallet.getActiveSessions() || {};
    web3Wallet.on("session_proposal", this.onSessionProposal.bind(this));
    web3Wallet.on("session_request", this.onSessionRequest.bind(this));
    web3Wallet.on("session_delete", (event) => __async(this, null, function* () {
      const session = sessions[event.topic];
    }));
    Object.keys(sessions).forEach((sessionKey) => __async(this, null, function* () {
      try {
        const session = sessions[sessionKey];
        this.sessions[sessionKey] = new WalletConnect2Session({
          web3Wallet,
          session,
          provider
        });
        yield this.sessions[sessionKey].updateSession({
          chainId,
          accounts
        });
      } catch (err) {
      }
    }));
  }
  get events() {
    return events;
  }
  static init(options) {
    return __async(this, null, function* () {
      if (wc2ManagerInstance) {
        wc2ManagerInstance.provider = options.provider;
        return wc2ManagerInstance;
      }
      let core;
      try {
        core = new Core({
          projectId: options.projectId
        });
      } catch (err) {
      }
      let web3Wallet;
      try {
        web3Wallet = yield SingleEthereum.init({
          core,
          metadata: options.metadata || constants_default.CLIENT_OPTIONS.clientMeta
        });
      } catch (err) {
        web3Wallet = yield SingleEthereum.init({
          core,
          metadata: options.metadata || constants_default.CLIENT_OPTIONS.clientMeta
        });
      }
      const chainId = yield options.provider.request({
        method: "eth_chainId"
      });
      const accounts = yield options.provider.request({
        method: "eth_requestAccounts"
      });
      wc2ManagerInstance = new _WC2Manager({ web3Wallet, chainId, accounts, provider: options.provider });
      return wc2ManagerInstance;
    });
  }
  deinit() {
    wc2ManagerInstance = void 0;
    events.removeAllListeners();
    this.removeAll().catch((error) => void 0);
  }
  getSessions() {
    const actives = this.web3Wallet.getActiveSessions() || {};
    const sessions = [];
    Object.keys(actives).forEach((sessionKey) => __async(this, null, function* () {
      const session = actives[sessionKey];
      sessions.push(session);
    }));
    return sessions;
  }
  removeSession(session) {
    return __async(this, null, function* () {
      try {
        yield this.web3Wallet.disconnectSession({
          topic: session.topic,
          error: { code: 1, message: ERROR_MESSAGES.MANUAL_DISCONNECT }
        });
      } catch (err) {
        yield this.web3Wallet.engine.web3wallet.engine.signClient.session.delete(
          session.topic,
          getSdkError("USER_DISCONNECTED")
        );
      }
    });
  }
  removeAll() {
    return __async(this, null, function* () {
      const actives = this.web3Wallet.getActiveSessions() || {};
      Object.values(actives).forEach((session) => __async(this, null, function* () {
        this.web3Wallet.disconnectSession({
          topic: session.topic,
          error: { code: 1, message: ERROR_MESSAGES.MANUAL_DISCONNECT }
        }).catch((err) => {
        });
      }));
    });
  }
  removePendings() {
    return __async(this, null, function* () {
      const pending = this.web3Wallet.getPendingSessionProposals() || {};
      Object.values(pending).forEach((session) => __async(this, null, function* () {
        this.web3Wallet.rejectSession({
          id: session.id,
          error: { code: 1, message: ERROR_MESSAGES.AUTO_REMOVE }
        }).catch((err) => {
        });
      }));
      const requests = this.web3Wallet.getPendingSessionRequests() || [];
      requests.forEach((request) => __async(this, null, function* () {
        try {
          yield this.web3Wallet.rejectRequest({
            id: request.id,
            topic: request.topic,
            error: { code: 1, message: ERROR_MESSAGES.USER_REJECT }
          });
        } catch (err) {
        }
      }));
    });
  }
  onSessionProposal(proposal) {
    return __async(this, null, function* () {
      var _a, _b, _c;
      const { id, params } = proposal;
      const {
        proposer
      } = params;
      const url = (_a = proposer.metadata.url) != null ? _a : "";
      const name = (_b = proposer.metadata.name) != null ? _b : "";
      const description = (_c = proposer.metadata.description) != null ? _c : "";
      const icons = proposer.metadata.icons;
      const peerInfo = {
        peerId: id,
        peerMeta: {
          url,
          name,
          description,
          icons
        }
      };
      try {
        yield new Promise((resolve, reject) => {
          events.on(SessionRequestEvent.approved, (peerId) => {
            if (peerInfo.peerId === peerId) {
              resolve(true);
            }
          });
          events.on(SessionRequestEvent.rejected, (peerId) => {
            if (peerInfo.peerId === peerId) {
              reject(new Error(SessionRequestEvent.rejected));
            }
          });
          events.emit(SessionRequestEvent.request, peerInfo);
        });
      } catch (err) {
        yield this.web3Wallet.rejectSession({
          id: proposal.id,
          error: err.messaage === SessionRequestEvent.rejected ? getSdkError("USER_REJECTED_METHODS") : err
        });
        return;
      }
      try {
        const chainId = yield this.provider.request({
          method: "eth_chainId"
        });
        const accounts = yield this.provider.request({
          method: "eth_requestAccounts"
        });
        const activeSession = yield this.web3Wallet.approveSession({
          id,
          chainId: Number(chainId),
          accounts
        });
        const session = new WalletConnect2Session({
          session: activeSession,
          web3Wallet: this.web3Wallet,
          provider: this.provider
        });
        this.sessions[activeSession.topic] = session;
        const peerMeta = getPeerDataFromSessopn(activeSession);
        events.emit(SessionRequestEvent.connected, peerMeta);
      } catch (err) {
      }
    });
  }
  onSessionRequest(requestEvent) {
    return __async(this, null, function* () {
      try {
        const session = this.sessions[requestEvent.topic];
        if (!session) {
          yield this.web3Wallet.rejectRequest({
            topic: requestEvent.topic,
            id: requestEvent.id,
            error: { code: 1, message: ERROR_MESSAGES.INVALID_ID }
          });
          return;
        }
        const method = requestEvent.params.request.method;
        if (method === "wallet_switchEthereumChain" || method === "wallet_addEthereumChain") {
          yield __privateGet(this, _switchChainRequest).call(this, requestEvent, session);
          return;
        }
        yield session.handleRequest(requestEvent);
      } catch (err) {
      }
    });
  }
  connect(wcUri) {
    return __async(this, null, function* () {
      const uri = getRealUri(wcUri);
      if (!isValidWCURI(uri)) {
        throw new Error(`Invalid wallet connect uri: ${uri}`);
      }
      try {
        const params = parseUri_default(uri);
        const rawParams = getAllUrlParams(uri);
        if (rawParams.sessionTopic) {
          return;
        }
        const activeSession = this.getSessions().find(
          (session) => session.topic === params.topic || session.pairingTopic === params.topic
        );
        if (activeSession) {
          const peerMeta = getPeerDataFromSessopn(activeSession);
          events.emit(SessionRequestEvent.connected, peerMeta);
          return;
        }
        const cleanUri = uri.startsWith("wc://") ? uri.replace("wc://", "wc:") : uri;
        yield this.web3Wallet.core.pairing.pair({
          uri: cleanUri
        });
      } catch (err) {
        throw err;
      }
    });
  }
  approveSessionRequest(peerId) {
    events.emit(SessionRequestEvent.approved, peerId);
  }
  rejectSessionRequest(peerId) {
    events.emit(SessionRequestEvent.rejected, peerId);
  }
  approveSwitchChain(peerId) {
    events.emit(SwitchChainEvent.approved, peerId);
  }
  rejectSwitchChain(peerId) {
    events.emit(SwitchChainEvent.rejected, peerId);
  }
  isValidUri(wcUri) {
    return isValidWCURI(getRealUri(wcUri));
  }
};
var WC2Manager = _WC2Manager;
_switchChainRequest = new WeakMap();
export {
  SessionRequestEvent,
  SwitchChainEvent,
  WC2Manager
};
//# sourceMappingURL=index.js.map
